import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  fetchLesplanning,
  moveScheduledLes,
  restoreLessen,
} from "#/modules/les.planning/api.planning.server";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { translatedApiError } from "#/modules/util.errors/translate";
import { m } from "#/paraglide/messages";

const rangeSchema = z.object({ from: z.string(), to: z.string() });
export type PlanningRangeInput = z.infer<typeof rangeSchema>;

export const getLesplanning = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: PlanningRangeInput) => rangeSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await fetchLesplanning(data.from, data.to);
    if (Result.isError(result)) throw result.error;
    return { lessen: result.value.lessen };
  });

export const getPlanningKlassen = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = await getClassesForStartLesson();
    if (Result.isError(result)) throw result.error;
    return result.value.data ?? [];
  });

const moveSchema = z.object({
  lesId: z.string(),
  datum: z.string(),
  lesuurId: z.string(),
});
export type MoveScheduledLesInput = z.infer<typeof moveSchema>;

export const moveScheduledLesFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: MoveScheduledLesInput) => moveSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await moveScheduledLes(data);
    if (Result.isError(result)) {
      throw translatedApiError(result.error, {
        LesNotActief: () => m["planning.api_errors.les_not_actief"](),
      });
    }
    return result.value;
  });

export const restoreLessenFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: { lesIds: string[] }) =>
    z.object({ lesIds: z.array(z.string()).min(1) }).parse(data),
  )
  .handler(async ({ data }) => {
    const result = await restoreLessen(data.lesIds);
    if (Result.isError(result)) throw translatedApiError(result.error);
    return result.value;
  });
